import {createContext, useCallback, useContext, useEffect, useState} from 'react'
import {useMatch} from 'react-router-dom'
import {useErrorHandler} from '../../../../../_metronic/helpers/ErrorHandler'
import {useMapLayersV2} from '../../Map/MapLayersV2Context'
import * as GisDataAdapter from '../services/GisDataAdapter'

const ViewFieldContext = createContext()

export function useViewFieldContext() {
  return useContext(ViewFieldContext)
}

export const ViewFieldConsumer = ViewFieldContext.Consumer

export function ViewFieldProvider({children}) {
  const [loading, setLoading] = useState(true)
  const [field, setField] = useState(null)
  const [fieldId, setFieldId] = useState(null)

  const [loadingLands, setLoadingLands] = useState(true)
  const [lands, setLands] = useState([])

  const [loadingGaps, setLoadingGaps] = useState(true)
  const [gaps, setGaps] = useState([])

  const mapLayers = useMapLayersV2()
  const {errorHandler} = useErrorHandler()
  const fieldRoute = useMatch('/gis/field/view/:field_id/*')
  const landRoute = useMatch('/gis/field/view/:field_id/land/view/:land_id/*')
  const shouldFitField = fieldRoute?.params.field_id === fieldId && !landRoute

  const loadLands = useCallback(
    (targetFieldId = fieldId) => {
      if (!targetFieldId) {
        return
      }

      setLoadingLands(true)
      errorHandler(GisDataAdapter.listFieldLands(targetFieldId)).then((res) => {
        if (res?.ok) {
          setLands(res.data)
        }

        setLoadingLands(false)
      })
    },
    [errorHandler, fieldId]
  )

  const loadGaps = useCallback(
    (targetFieldId = fieldId) => {
      if (!targetFieldId) {
        return
      }

      setLoadingGaps(true)
      errorHandler(GisDataAdapter.listFieldGaps(targetFieldId)).then((res) => {
        if (res?.ok) {
          setGaps(res.data)
        }

        setLoadingGaps(false)
      })
    },
    [errorHandler, fieldId]
  )

  const load = useCallback(
    (callback) => {
      if (!fieldId) {
        return
      }

      setLoading(true)
      setLoadingLands(true)
      setLoadingGaps(true)
      if (shouldFitField) {
        mapLayers.fit(fieldId)
      }

      errorHandler(GisDataAdapter.getFieldBundle(fieldId)).then((res) => {
        if (res?.ok) {
          callback?.(res.data.field)
          setField(res.data.field)
          setLands(res.data.lands)
          setGaps(res.data.gaps)
        }

        setLoading(false)
        setLoadingLands(false)
        setLoadingGaps(false)
      })
    },
    [errorHandler, fieldId, mapLayers, shouldFitField]
  )

  useEffect(() => {
    if (fieldId != null) {
      load()
    }
  }, [fieldId])

  useEffect(() => {
    if (shouldFitField && field?.field_id === fieldId) {
      return mapLayers.fit(field.field_id)
    }
    // Refit on field entry, not a late bundle response while a land or another route owns the map.
    // mapLayers context value is recreated every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field?.field_id, fieldId, shouldFitField])

  const value = {
    field,
    setField,
    setFieldId,
    loading,
    load,
    lands,
    loadingLands,
    gaps,
    loadingGaps,
    loadLands,
    loadGaps,
  }

  return <ViewFieldContext.Provider value={value}>{children}</ViewFieldContext.Provider>
}
